import { defineMessages, type MessageDescriptor } from 'react-intl';

type MessageKeys =
	| 'sideNavLabel'
	| 'contentHeader'
	| 'heading'
	| 'description'
	| 'openSharedChatLabel'
	| 'deleteSharedChatLinkLabel'
	| 'truncatedTitle'
	| 'unshareTooltip'
	| 'accessUpdatedFlagTitle'
	| 'accessUpdateErrorFlagTitle'
	| 'accessUpdateErrorFlagDescription'
	| 'loadingLabel'
	| 'emptyMessage'
	| 'errorMessage'
	| 'retryButton';

export const messages: Record<MessageKeys, MessageDescriptor> = defineMessages({
	sideNavLabel: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.label',
		defaultMessage: 'Shared chats',
		description: 'Side nav item label for managing shared chat links in the Rovo Settings modal',
	},
	contentHeader: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.content-header',
		defaultMessage: 'Rovo settings',
		description: 'Content header displayed at the top of the shared chats settings section',
	},
	heading: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.heading',
		defaultMessage: 'Shared chats',
		description: 'Heading for the shared chats settings section',
	},
	description: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.description',
		defaultMessage: 'Manage links to copies of your chats.',
		description:
			'Description explaining that the shared chats settings section lets users manage links to shared copies of their chats.',
	},
	openSharedChatLabel: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.open-shared-chat-label',
		defaultMessage: 'Open shared chat: {title}',
		description:
			'Accessible label for an icon button that opens a shared chat link. {title} is the shared chat title.',
	},
	deleteSharedChatLinkLabel: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.delete-shared-chat-link-label',
		defaultMessage: 'Delete shared chat link: {title}',
		description:
			'Accessible label for an icon button that deletes a shared chat link. {title} is the shared chat title.',
	},
	truncatedTitle: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.truncated-title',
		defaultMessage: '{titlePrefix}…',
		description:
			'Text shown when a shared chat title is shortened to fit in the available row width. {titlePrefix} is the visible beginning of the user-generated shared chat title. Preserve a locale-appropriate truncation marker.',
	},
	unshareTooltip: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.unshare-tooltip',
		defaultMessage: 'Stop sharing',
		description:
			'Tooltip shown beside the delete icon button for a shared chat, indicating that the button stops sharing the selected chat link.',
	},
	accessUpdatedFlagTitle: {
		id: 'rovo-settings-modal.rovo-settings-modal.side-nav-modal-provider.shared-chats-content.accessUpdatedFlagTitle.ai-non-final',
		defaultMessage: 'Access updated',
		description:
			'Title of the success notification flag shown after access to a shared chat link has been updated.',
	},
	accessUpdateErrorFlagTitle: {
		id: 'rovo-settings-modal.rovo-settings-modal.side-nav-modal-provider.shared-chats-content.accessUpdateErrorFlagTitle.ai-non-final',
		defaultMessage: 'Could not update access',
		description:
			'Title of the error notification flag shown when access to a shared chat link could not be updated.',
	},
	accessUpdateErrorFlagDescription: {
		id: 'rovo-settings-modal.rovo-settings-modal.side-nav-modal-provider.shared-chats-content.accessUpdateErrorFlagDescription.ai-non-final',
		defaultMessage: 'Something went wrong. Try again in a few minutes.',
		description:
			'Description in the access update error notification flag prompting the user to retry after a short wait.',
	},
	loadingLabel: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.loading-label',
		defaultMessage: 'Loading shared chats',
		description: 'Accessible label for the spinner shown while shared chat links are loading',
	},
	emptyMessage: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.empty-message',
		defaultMessage: "You haven't shared any chats yet.",
		description: 'Message shown in the shared chats section when the user has not shared any chats',
	},
	errorMessage: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.error-message',
		defaultMessage: "We couldn't load your shared chats.",
		description: 'Message shown when loading the list of shared chat links fails',
	},
	retryButton: {
		id: 'ai-mate.rovo-settings-modal.side-nav-modal.shared-chats.retry-button',
		defaultMessage: 'Try again',
		description: 'Button label to retry loading shared chat links after an error',
	},
});
